import dayjs, { type Dayjs } from 'dayjs';

/**
 * One end of a date range. `dynamic` is a day offset from today (`0` is
 * Today), resolved whenever the range is applied; `fixed` is a `YYYY-MM-DD`
 * date.
 */
export type DateBound =
    | { type: 'dynamic'; value: number }
    | { type: 'fixed'; value: string };

export type DateRangeSpec = { start: DateBound; end: DateBound };

export type DateRangeLabels = {
    /** Preset label for a trailing window, e.g. "Last 7 Days". */
    lastNDays: (days: number) => string;
    today: string;
    /** Joins the two dates, e.g. "Sep 25 to Oct 1". */
    to: string;
    endBeforeStart: string;
};

export const DEFAULT_DATE_RANGE_PRESETS = [7, 30, 60];

const DAY_FORMAT = 'YYYY-MM-DD';
const LABEL_FORMAT = 'MMM D';

export const TODAY_BOUND: DateBound = { type: 'dynamic', value: 0 };

export const fixedBound = (date: Dayjs): DateBound => ({
    type: 'fixed',
    value: date.format(DAY_FORMAT),
});

/** `n` calendar days ending on and including today. */
export const lastNDays = (n: number): DateRangeSpec => ({
    start: { type: 'dynamic', value: -(n - 1) },
    end: TODAY_BOUND,
});

const resolveBound = (bound: DateBound, today: Dayjs): Dayjs =>
    bound.type === 'dynamic'
        ? today.add(bound.value, 'day')
        : dayjs(bound.value, DAY_FORMAT);

/** Resolves both bounds against `today` as an inclusive start/end-of-day pair. */
export const resolveDateRange = (
    spec: DateRangeSpec,
    today: Dayjs = dayjs(),
): [Dayjs, Dayjs] => [
    resolveBound(spec.start, today).startOf('day'),
    resolveBound(spec.end, today).endOf('day'),
];

export const isValidDateRange = (
    spec: DateRangeSpec,
    today: Dayjs = dayjs(),
): boolean => {
    const [start, end] = resolveDateRange(spec, today);
    return start.isValid() && end.isValid() && !end.isBefore(start);
};

/** Whether `spec` still resolves to the given start and end days. */
export const matchesDateRange = (
    spec: DateRangeSpec,
    start: Dayjs,
    end: Dayjs,
    today: Dayjs = dayjs(),
): boolean => {
    const [resolvedStart, resolvedEnd] = resolveDateRange(spec, today);
    return resolvedStart.isSame(start, 'day') && resolvedEnd.isSame(end, 'day');
};

const isToday = (bound: DateBound) =>
    bound.type === 'dynamic' && bound.value === 0;

/** The `N` of a "Last N Days" preset, or `undefined` for any other spec. */
export const getPresetDays = (
    spec: DateRangeSpec,
    presets: number[] = DEFAULT_DATE_RANGE_PRESETS,
): number | undefined => {
    const { start, end } = spec;
    if (start.type !== 'dynamic' || !isToday(end)) return undefined;
    return presets.find(days => start.value === -(days - 1));
};

export const formatDateRangeLabel = (
    spec: DateRangeSpec,
    labels: DateRangeLabels,
    today: Dayjs = dayjs(),
    presets: number[] = DEFAULT_DATE_RANGE_PRESETS,
): string => {
    const [start, end] = resolveDateRange(spec, today);
    const startText = start.format(LABEL_FORMAT);
    const endText = end.format(LABEL_FORMAT);
    const days = getPresetDays(spec, presets);

    if (days !== undefined && days > 1) {
        return `${labels.lastNDays(days)} (${startText} ${labels.to} ${endText})`;
    }
    if (isToday(spec.start) && isToday(spec.end)) {
        return `${labels.today} (${endText})`;
    }

    const endLabel = isToday(spec.end)
        ? `${labels.today} (${endText})`
        : endText;
    return `${startText} ${labels.to} ${endLabel}`;
};
